import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { Lock, Clock, Sparkle, Warning, SpinnerGap } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function SharedTalkingDocPage() {
  const { shareId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [outline, setOutline] = useState(null);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);

  const fetchMeta = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/talking-doc/share/public/${shareId}`);
      setMeta(resp.data);
      if (resp.data.outline !== undefined) {
        setOutline(resp.data.outline);
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "This link is no longer available.");
    }
    setLoading(false);
  }, [shareId]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (!password.trim()) return;
    setVerifying(true);
    try {
      const resp = await axios.post(`${API}/talking-doc/share/public/${shareId}/verify`, { password });
      setOutline(resp.data.outline);
      setMeta(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect password");
    }
    setVerifying(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <SpinnerGap size={32} className="animate-spin text-[#FF4400]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-4" data-testid="share-error">
          <div className="w-14 h-14 mx-auto rounded-xl bg-red-500/10 flex items-center justify-center">
            <Warning size={28} className="text-red-400" weight="duotone" />
          </div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)] font-['Cormorant_Garamond']">Link unavailable</h1>
          <p className="text-sm text-[var(--text-muted)]">{error}</p>
          <Link to="/" className="inline-block text-xs text-[#FF4400] hover:text-[#FF6633] font-medium">
            Go to Quibn →
          </Link>
        </div>
      </div>
    );
  }

  const needsPassword = meta?.password_protected && !outline;

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex flex-col">
      {/* Header */}
      <header className="border-b border-[var(--border)] bg-[var(--bg-card)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="text-lg font-['Cormorant_Garamond'] font-bold text-[var(--text-primary)]">Quibn</Link>
          <Link to="/" className="text-xs text-[var(--text-muted)] hover:text-[#FF4400] font-medium transition-colors" data-testid="header-create-link">
            Create your own →
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {needsPassword ? (
          <div className="max-w-sm mx-auto space-y-5 mt-12" data-testid="password-gate">
            <div className="w-12 h-12 mx-auto rounded-xl bg-[#FF4400]/10 flex items-center justify-center">
              <Lock size={22} className="text-[#FF4400]" weight="duotone" />
            </div>
            <div className="text-center space-y-1">
              <h1 className="text-xl font-semibold text-[var(--text-primary)] font-['Cormorant_Garamond']">Password required</h1>
              <p className="text-xs text-[var(--text-muted)]">
                The creator of this outline has protected it with a password.
              </p>
            </div>
            <form onSubmit={handleVerify} className="space-y-3">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm"
                autoFocus
                data-testid="password-input"
              />
              <Button
                type="submit"
                disabled={verifying || !password.trim()}
                className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white"
                data-testid="unlock-btn"
              >
                {verifying ? "Unlocking…" : "Unlock Outline"}
              </Button>
            </form>
          </div>
        ) : (
          <article className="space-y-6" data-testid="shared-outline-article">
            <div className="space-y-2 pb-4 border-b border-[var(--border)]">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond'] tracking-tight" data-testid="shared-outline-title">
                {meta?.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap text-[11px] text-[var(--text-muted)]">
                <span className="inline-flex items-center gap-1"><Clock size={10} /> Shared {formatDate(meta?.created_at)}</span>
                {meta?.expires_at && <span>· Expires {formatDate(meta.expires_at)}</span>}
                <span>· {meta?.view_count || 0} view{meta?.view_count === 1 ? "" : "s"}</span>
              </div>
            </div>

            <div
              className="prose prose-sm max-w-none text-[var(--text-secondary)] leading-relaxed
                prose-headings:font-semibold prose-headings:text-[var(--text-primary)]
                prose-h1:text-xl prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2
                prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1.5
                prose-strong:font-semibold prose-strong:text-[var(--text-primary)]
                prose-blockquote:border-l-[#FF4400] prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
                prose-p:my-2 prose-ul:my-2 prose-ol:my-2
                prose-a:text-[#FF4400] hover:prose-a:text-[#FF6633]"
              data-testid="shared-outline-body"
            >
              <ReactMarkdown>{outline || ""}</ReactMarkdown>
            </div>
          </article>
        )}
      </main>

      {/* Footer — "Made with Quibn" badge */}
      <footer className="border-t border-[var(--border)] bg-[var(--bg-card)] py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
            <Sparkle size={12} weight="duotone" className="text-[#FF4400]" />
            <span>Made with</span>
            <Link to="/" className="font-['Cormorant_Garamond'] font-bold text-[var(--text-primary)] hover:text-[#FF4400] transition-colors" data-testid="footer-quibn-link">
              Quibn
            </Link>
            <span>— where writing begins.</span>
          </div>
          <Link
            to="/"
            className="text-[11px] font-medium text-[#FF4400] hover:text-[#FF6633] transition-colors"
            data-testid="footer-cta"
          >
            Build your own talking doc →
          </Link>
        </div>
      </footer>
    </div>
  );
}
